import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Space, Typography, Upload, message } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { PaperClipOutlined, SendOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { CrmRichTextEditor, isQuillEmpty } from '../crm/CrmRichTextEditor';
import {
    useGetMailSendersQuery,
    useLazySearchMailRecipientsQuery,
    useSendMailMutation,
    useUploadMailAttachmentMutation,
} from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';
import { IMailMessage, IMailStagedAttachment, IMailThread } from '../../../services/nestjs-base-api/types';

const { Text } = Typography;

// Kept in step with MailAttachmentsService.MAX_ATTACHMENT_BYTES: rejecting here
// saves composing a whole message only to have the send refuse it.
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const cleanAddresses = (values: string[]): string[] =>
    values.flatMap((value) => value.split(/[,;\s]+/))
        .map((address) => address.trim())
        .filter(Boolean);

// Gmail-style attribution line. Built client-side so the sender sees the quote
// before it goes out and can trim it, which is how every real mail client works.
const quoteBlock = (message: IMailMessage, locale: string): string => {
    const when = new Date(message.occurredAt).toLocaleString(locale, {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    const who = message.fromName ? `${message.fromName} &lt;${message.from}&gt;` : message.from;
    const body = message.bodyHtml ?? '';

    return `<p><br></p><p>On ${when}, ${who} wrote:</p>` +
        `<blockquote style="border-left:2px solid #e0e0e0;padding-left:12px;color:#555;margin:0">${body}</blockquote>`;
};

interface MailComposerProps {
    open: boolean;
    onClose: () => void;
    /** Set when replying; drives quoting, threading headers and the default sender. */
    thread?: IMailThread | null;
    replyAll?: boolean;
    onSent?: (threadKey: string) => void;
}

export const MailComposer: React.FC<MailComposerProps> = ({ open, onClose, thread, replyAll, onSent }) => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken as string;

    const { data: senders } = useGetMailSendersQuery({ jwtToken }, { skip: !jwtToken });
    const [sendMail, sendResult] = useSendMailMutation();
    const [uploadAttachment] = useUploadMailAttachmentMutation();

    const [senderUserId, setSenderUserId] = useState<number | undefined>();
    const [to, setTo] = useState<string[]>([]);
    const [cc, setCc] = useState<string[]>([]);
    const [recipientOptions, setRecipientOptions] = useState<DefaultOptionType[]>([]);
    const [searchRecipients] = useLazySearchMailRecipientsQuery();
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [attachments, setAttachments] = useState<IMailStagedAttachment[]>([]);
    const [uploading, setUploading] = useState(false);

    const lastMessage = thread?.messages?.[thread.messages.length - 1];
    const lastInbound = useMemo(
        () => [...(thread?.messages ?? [])].reverse().find((m) => m.direction === 'in'),
        [thread],
    );

    useEffect(() => {
        if (!open) return;

        if (thread && lastMessage) {
            const replyTarget = lastInbound ?? lastMessage;
            setTo(replyTarget.direction === 'in' ? [replyTarget.from] : replyTarget.to);
            setCc(replyAll ? replyTarget.cc : []);
            setSubject(/^re:/i.test(thread.subject) ? thread.subject : `Re: ${thread.subject}`);
            setBody(quoteBlock(replyTarget, intl.locale));

            // Reply as whichever of our addresses the thread was written to, so
            // the conversation stays consistent for the recipient regardless of
            // who happens to be typing.
            const ourAddresses = new Map((senders ?? []).map((s) => [s.address.toLowerCase(), s.userId]));
            const addressed = [...(replyTarget.to ?? []), ...(replyTarget.cc ?? [])]
                .map((a) => ourAddresses.get(a.toLowerCase()))
                .find(Boolean);
            setSenderUserId(addressed ?? user?.id);
        } else {
            setTo([]);
            setCc([]);
            setSubject('');
            setBody('');
            setSenderUserId(user?.id);
        }
        setAttachments([]);
    }, [open, thread, lastMessage, lastInbound, replyAll, senders, user?.id, intl.locale]);

    const handleUpload = async (file: File): Promise<boolean> => {
        if (file.size > MAX_ATTACHMENT_BYTES) {
            message.error(intl.formatMessage({ id: 'mail.attachmentTooLarge' }));
            return false;
        }
        setUploading(true);
        try {
            const staged = await uploadAttachment({ jwtToken, file }).unwrap();
            setAttachments((current) => [...current, staged]);
        } catch (error: any) {
            message.error(error?.data?.message || intl.formatMessage({ id: 'mail.attachmentFailed' }));
        } finally {
            setUploading(false);
        }
        return false;
    };

    // The address book covers platform users, CRM contacts and anyone we have
    // corresponded with. Typing a brand-new address still works — the field
    // accepts free entry and only offers these as suggestions.
    const lookUpRecipients = async (value: string) => {
        if (value.trim().length < 2) {
            setRecipientOptions([]);
            return;
        }
        try {
            const matches = await searchRecipients({ jwtToken, q: value }).unwrap();
            setRecipientOptions(
                matches.map((match) => ({
                    value: match.email,
                    label: match.name ? `${match.name} <${match.email}>` : match.email,
                })),
            );
        } catch {
            setRecipientOptions([]);
        }
    };

    const handleSend = async () => {
        const toList = cleanAddresses(to);
        if (toList.length === 0) {
            message.error(intl.formatMessage({ id: 'mail.recipientRequired' }));
            return;
        }
        if (!subject.trim()) {
            message.error(intl.formatMessage({ id: 'mail.subjectRequired' }));
            return;
        }
        if (isQuillEmpty(body)) {
            message.error(intl.formatMessage({ id: 'mail.bodyRequired' }));
            return;
        }
        if (!senderUserId) {
            message.error(intl.formatMessage({ id: 'mail.senderRequired' }));
            return;
        }

        try {
            const result = await sendMail({
                jwtToken,
                senderUserId,
                to: toList,
                cc: cleanAddresses(cc),
                subject: subject.trim(),
                bodyHtml: body,
                attachmentIds: attachments.map((a) => a.id),
                // References carries the CHAIN, oldest first, starting at the
                // conversation root. Sending only the last id would make the
                // reply look like the start of a new conversation to both the
                // recipient's client and our own thread keying.
                inReplyTo: lastMessage?.messageId,
                references: thread
                    ? [thread.threadKey, lastMessage?.messageId]
                          .filter((id, index, all): id is string => !!id && all.indexOf(id) === index)
                          .join(' ')
                    : undefined,
                crmCompanyId: thread?.account?.crmCompanyId,
            }).unwrap();

            message.success(intl.formatMessage({ id: 'mail.sent' }));
            onSent?.(result.threadKey);
            onClose();
        } catch (error: any) {
            message.error(error?.data?.message || intl.formatMessage({ id: 'mail.sendFailed' }));
        }
    };

    const impersonating = senderUserId !== undefined && senderUserId !== user?.id;
    const senderOptions = (senders ?? []).map((sender) => ({
        value: sender.userId,
        label: `${sender.fromLabel} <${sender.address}>`,
    }));

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={780}
            title={thread
                ? <FormattedMessage id="mail.composer.replyTitle" defaultMessage="Reply" />
                : <FormattedMessage id="mail.composer.newTitle" defaultMessage="New message" />}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    <FormattedMessage id="mail.cancel" defaultMessage="Cancel" />
                </Button>,
                <Button
                    key="send"
                    type="primary"
                    icon={<SendOutlined />}
                    loading={sendResult.isLoading}
                    disabled={uploading}
                    onClick={handleSend}
                >
                    <FormattedMessage id="mail.send" defaultMessage="Send" />
                </Button>,
            ]}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {/* Hidden at one option: with a single sendable address the picker
                    is a decision the sender does not have. */}
                {senderOptions.length > 1 && (
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <FormattedMessage id="mail.from" defaultMessage="From" />
                        </Text>
                        <Select
                            style={{ width: '100%' }}
                            value={senderUserId}
                            options={senderOptions}
                            onChange={setSenderUserId}
                        />
                    </div>
                )}

                {impersonating && (
                    <Alert
                        type="warning"
                        showIcon
                        message={intl.formatMessage({ id: 'mail.impersonationWarning' })}
                    />
                )}

                <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder={intl.formatMessage({ id: 'mail.toPlaceholder' })}
                    value={to}
                    onChange={setTo}
                    onSearch={lookUpRecipients}
                    options={recipientOptions}
                    filterOption={false}
                    tokenSeparators={[',', ';', ' ']}
                    notFoundContent={null}
                />
                <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder={intl.formatMessage({ id: 'mail.ccPlaceholder' })}
                    value={cc}
                    onChange={setCc}
                    onSearch={lookUpRecipients}
                    options={recipientOptions}
                    filterOption={false}
                    tokenSeparators={[',', ';', ' ']}
                    notFoundContent={null}
                />
                <Input
                    placeholder={intl.formatMessage({ id: 'mail.subjectPlaceholder' })}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                />

                <CrmRichTextEditor value={body} onChange={setBody} minHeight={260} />

                <Space wrap>
                    <Upload beforeUpload={handleUpload} showUploadList={false} multiple>
                        <Button icon={<PaperClipOutlined />} loading={uploading}>
                            <FormattedMessage id="mail.attach" defaultMessage="Attach file" />
                        </Button>
                    </Upload>
                    {attachments.map((attachment) => (
                        <Text key={attachment.id} type="secondary" style={{ fontSize: 12 }}>
                            {attachment.filename} ({Math.round(attachment.size / 1024)}KB)
                        </Text>
                    ))}
                </Space>
            </Space>
        </Modal>
    );
};
